const MAX_FILE_SIZE = 10 * 1024 * 1024 * 1024;
const TTL = 48 * 60 * 60 * 1000;

const readBlob = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Unable to read the selected file."));
    reader.readAsArrayBuffer(blob);
});

export const fingerprintFile = async (file) => {
    if (!window.crypto || !window.crypto.subtle) {
        throw new Error("Resumable uploads need a secure connection. Open the site using HTTPS (or localhost for local testing).");
    }
    // Sample both ends without reading a multi-GB video into browser memory.
    const sample = new Blob([JSON.stringify([file.name, file.size, file.type, file.lastModified]),
        file.slice(0, 65536), file.slice(Math.max(0, file.size - 65536))]);
    const digest = await window.crypto.subtle.digest("SHA-256", await readBlob(sample));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
};

const newId = () => Array.from(window.crypto.getRandomValues(new Uint8Array(16)),
    (byte) => byte.toString(16).padStart(2, "0")).join("");
const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
const retryable = (error) => !error.status || [408, 409, 500, 502, 503, 504, 520, 522, 524].includes(error.status);

export const resumableUpload = async ({ file, title, caption, userId, apiBase, send,
    onUploadProgress = () => {}, onUploadStatus = () => {},
    storage = window.localStorage, fingerprint = fingerprintFile, delay = wait, makeId = newId }) => {
    if (!file || !file.size || file.size > MAX_FILE_SIZE) throw new Error("Choose a file up to 10 GB.");
    if (!title.trim()) throw new Error("A title is required before publishing.");
    onUploadStatus("Preparing upload...");
    const signature = await fingerprint(file);
    const key = `pl-upload-v1:${apiBase}:${userId}:${signature}`;
    let saved;
    try { saved = JSON.parse(storage.getItem(key)); } catch (error) { saved = null; }
    if (!saved || !/^[a-f0-9]{32}$/.test(saved.id) || saved.expires <= Date.now()) {
        saved = { id: makeId(), expires: Date.now() + TTL };
    }
    // Only metadata is stored. The browser will ask the user to reselect the file after a reload.
    try { storage.setItem(key, JSON.stringify(saved)); }
    catch (error) { throw new Error("Allow browser storage to save upload progress, then try again."); }
    const retry = async (operation) => {
        for (let attempt = 0; ; attempt += 1) {
            try { return await operation(); }
            catch (error) {
                if (!retryable(error) || attempt >= 3) throw error;
                onUploadStatus(`Connection interrupted. Retrying (${attempt + 1}/3)...`);
                await delay(1000 * (2 ** attempt));
            }
        }
    };
    const json = (path, body) => send({ path, method: "POST", formData: JSON.stringify(body || {}), contentType: "application/json" });
    const base = `/uploads/${saved.id}`;
    const validate = (state) => {
        if (!state || state.uploadId !== saved.id || state.size !== file.size
            || !Number.isSafeInteger(state.offset) || state.offset < 0 || state.offset > file.size
            || !Number.isSafeInteger(state.chunkSize) || state.chunkSize <= 0 || state.chunkSize > 4 * 1024 * 1024) {
            throw Object.assign(new Error("The server returned invalid upload progress."), { status: 400 });
        }
        return state;
    };
    try {
        let state = validate(await retry(() => json("/uploads", {
            uploadId: saved.id, name: file.name, type: file.type, size: file.size,
            fingerprint: signature, title, caption,
        })));
        onUploadProgress(Math.floor(state.offset / file.size * 100));
        while (!state.postId && state.offset < file.size) {
            const previousOffset = state.offset;
            state = await retry(async () => {
                // A failed response may still have saved the chunk; always ask before retrying.
                const current = validate(await send({ path: base, method: "GET" }));
                if (current.postId || current.offset > previousOffset) return current;
                const end = Math.min(current.offset + current.chunkSize, file.size);
                onUploadStatus("Uploading...");
                return validate(await send({ path: `${base}/chunks/${current.offset}`, method: "PUT",
                    formData: file.slice(current.offset, end), contentType: "application/octet-stream",
                    onUploadProgress: (percent) => onUploadProgress(Math.min(99, Math.floor(
                        (current.offset + (end - current.offset) * percent / 100) / file.size * 100))),
                }));
            });
            if (!state.postId && state.offset <= previousOffset) throw new Error("The server did not save the upload chunk.");
            onUploadProgress(Math.floor(state.offset / file.size * 100));
        }
        onUploadStatus("Publishing post...");
        const result = await retry(() => json(`${base}/complete`));
        if (!result || !result.postId || !Array.isArray(result.posts)) {
            throw new Error("The server did not confirm publication.");
        }
        storage.removeItem(key);
        return result;
    } catch (error) {
        if (error.status === 410 || error.status === 404) storage.removeItem(key);
        throw new Error(`${error.message} Select the same file and press Publish post to resume. Unfinished uploads expire after 48 hours.`);
    }
};
